import { LoadingOutlined } from "@ant-design/icons"
import { Expense, ExpenseStatusEnum } from "@finway-group/shared/lib/models"
import { Button, Table, Tag } from "antd"
import { ColumnsType } from "antd/lib/table"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import { EmptyTableData } from "~Components/emptyTableData"
import { ExpenseAmountColumn } from "~Components/expenseAmountColumn"
import { ExpenseReminderInfo } from "~Components/expenseReminderInfo"
import { ExpenseStatusTag } from "~Components/expenseStatusTag"
import { ExpenseTagColumn } from "~Components/expenseTagColumn"
import { ErrorTable } from "~Components/layout/errorTable"
import { Loading } from "~Components/loading"
import { RequestColumn } from "~Components/requestColumn"
import { ExpandTripFolderTableIcon } from "~Components/tables/expandTripFolderTableIcon"
import { useLoadingExpenseIds, useSelectedExpenses } from "~Shared/context/selectedExpenses.context"
import { useCompany } from "~Shared/hooks/company.hooks"
import { markExpenseAsReviewed, reviewFolder, useIsExpenseAccountMandatory } from "~Shared/hooks/expense.hooks"
import { useFetchTable, useTable, useTableFilterAndSearchLoading, useTableSearchString } from "~Shared/hooks/table.hooks"
import { useManyVendors } from "~Shared/queries/vendors/vendor.queries"
import { AuthzService, UserService } from "~Shared/services"
import { setExpense } from "~Shared/store/actions/expense/expenseActions"
import { refetchTable, updateTableOnChange } from "~Shared/store/actions/tables/tableActions"
import { TablesEnum } from "~Shared/store/reducers/tableConfigReducer"
import { checkBelongsToAFolder, checkIsExpenseReviewClickable, findFolderExpense, getVendorIdsFromReviewTableExpenses, isFolderExpense } from "~Shared/utils/expense.utils"
import { insertIf } from "~Shared/utils/helper.utils"
import { getSortOrderForColumn } from "~Shared/utils/table.utils"
import { ExpenseActionButtonTooltip } from "../expenseDetails/expenseActionButton/expenseActionButton.tooltip"

export const ReviewTab = () => {
    const { t } = useTranslation()
    const history = useHistory()
    const dispatch = useDispatch()
    const loggedInProfile = UserService.getLoggedInEmployeeProfile()
    const [selectedExpenses, setSelectedExpenses] = useSelectedExpenses()
    const [loadingExpenseIds, setLoadingExpenseIds] = useLoadingExpenseIds()
    const [expandedRowKeys, setExpandedRowKeys] = useState<Array<React.Key>>([])
    const reviewTable = useTable<Expense>(TablesEnum.REVIEW_REQUESTS)
    const tabLoading = useTableFilterAndSearchLoading(TablesEnum.REVIEW_REQUESTS)

    const { taggingEnabled, gobdCompliance } = useCompany()
    const enforceGobdCompliantInvoice = !!gobdCompliance?.enforceGobdCompliantInvoice
    const showTags = taggingEnabled && reviewTable.data.docs.some((expense) => expense?.expenseTags?.length)
    const searchString = useTableSearchString(TablesEnum.REVIEW_REQUESTS)
    const isExpenseAccountMandatory = useIsExpenseAccountMandatory()

    const hasReviewRight = AuthzService.canLoggedInUserReviewExpenses()
    const vendorIds = hasReviewRight ? getVendorIdsFromReviewTableExpenses(reviewTable.data.docs) : []

    const { data: vendors, isInitialLoading: isLoadingVendors } = useManyVendors(vendorIds, { excludeDeleted: false })

    useFetchTable(TablesEnum.REVIEW_REQUESTS)

    const renderActionColumn = (data: Expense) => {
        const shouldShowExpenseStatusTag = data.status !== ExpenseStatusEnum.APPROVED || !hasReviewRight
        if (shouldShowExpenseStatusTag) {
            return <ExpenseStatusTag status={data.status} />
        }

        const isButtonLoading = loadingExpenseIds.includes(data.id) || isLoadingVendors

        const isClickable = checkIsExpenseReviewClickable({
            expense: data,
            vendors,
            isCompanyGobdCompliant: enforceGobdCompliantInvoice,
            isExpenseAccountMandatory,
        })

        return (
            <div className="btn-wrapper">
                <ExpenseActionButtonTooltip
                    vendors={vendors}
                    expense={data}
                    subExpenses={isFolderExpense(data) ? data.children : undefined}
                    placement="right"
                    shouldShowTooltip={!isClickable}
                >
                    {data.status < ExpenseStatusEnum.APPROVED ? (
                        <Tag className="ant-tag-red ml-10">Pending approval state</Tag>
                    ) : (
                        <Button
                            loading={isButtonLoading}
                            className="mr-auto ml-auto"
                            onClick={(e) => {
                                e.stopPropagation()
                                setLoadingExpenseIds([data.id])
                                if (isFolderExpense(data)) {
                                    return reviewFolder(data, (data as any).children)
                                }

                                return markExpenseAsReviewed(data).finally(() => setLoadingExpenseIds([]))
                            }}
                            disabled={!isClickable}
                            type="primary"
                        >
                            <span>{t("action:mark_as_reviewed")}</span>
                        </Button>
                    )}
                </ExpenseActionButtonTooltip>
            </div>
        )
    }

    const expandableColumn = reviewTable.data.docs.some((expense) => (expense as any)?.children && (expense as any)?.children.length > 0)
        ? [
              {
                  dataIndex: "expandIcon",
                  width: 30,
                  align: "center",
              } as any,
          ]
        : []

    const columns: ColumnsType<Expense> = [
        ...expandableColumn,
        {
            title: t("label:request"),
            key: "expenseNumber",
            align: "left",
            ellipsis: true,
            width: 350,
            ...(loggedInProfile.settings.showExpenseNumbers && {
                sorter: true,
                sortOrder: getSortOrderForColumn(reviewTable, "expenseNumber"),
            }),
            render: (data: any) => <RequestColumn request={data} />,
        },
        ...insertIf(showTags, {
            title: t("label:tag"),
            key: "tag",
            align: "left",
            width: 130,
            render: ({ expenseTags: expenseTagIds }: Expense) => <ExpenseTagColumn expenseTagIds={expenseTagIds} />,
        }),
        {
            key: "reminder",
            align: "center",
            width: 50,
            render: (request: Expense) => <ExpenseReminderInfo request={request} />,
        },
        {
            title: t("label:action"),
            key: "action",
            align: "center",
            width: 260,
            render: (expense: Expense) => renderActionColumn(expense),
        },
        {
            title: loggedInProfile.settings.showGrossAmount ? t("label:gross_amount") : t("label:net_amount"),
            key: loggedInProfile.settings.showGrossAmount ? "totalGrossPrice" : "totalNetPrice",
            align: "right",
            className: "pr-10",
            width: 200,
            sorter: true,
            sortOrder: getSortOrderForColumn(reviewTable, loggedInProfile.settings.showGrossAmount ? "totalGrossPrice" : "totalNetPrice"),
            render: (data: Expense) => <ExpenseAmountColumn request={data} showGross={loggedInProfile.settings.showGrossAmount} />,
        },
    ]

    return tabLoading ? (
        <Loading />
    ) : reviewTable.error ? (
        <ErrorTable onTableReload={() => dispatch(refetchTable(TablesEnum.REVIEW_REQUESTS))} isLoading={reviewTable.isFetching} />
    ) : reviewTable.data.totalDocs === 0 && !reviewTable.isFetching ? (
        <EmptyTableData isSearchOrFilterActive={!!searchString} title={t("info:empty_state.requests.title")} message={t("info:empty_state.review.message")} />
    ) : (
        <div className="overflow-auto mb-24 p-2 animation-appear">
            <ul className="card-list">
                <Table
                    rowKey={(record) => record.id}
                    dataSource={reviewTable.data.docs}
                    columns={columns}
                    rowSelection={{
                        type: "checkbox",
                        renderCell: (_value, _record, _index, originNode) => (
                            <div onClick={(e) => e.stopPropagation()} className="bigger-hitbox-checkbox">
                                {originNode}
                            </div>
                        ),
                        selectedRowKeys: selectedExpenses.map((record) => record.id),
                        checkStrictly: false,
                        onChange: (_selectedRowKeys, selectedRows) => {
                            setSelectedExpenses(selectedRows)
                        },
                        onSelectAll: (selected, selectedRows) => {
                            // * Deselect all on bulk deselect
                            if (!selected && !selectedRows.length) {
                                setSelectedExpenses([])
                                return
                            }
                            setSelectedExpenses(selectedRows)
                            const selectedSubExpenses = selectedRows.filter(checkBelongsToAFolder)
                            const subExpensesFolders = selectedSubExpenses
                                .map((expense) => findFolderExpense(reviewTable.data.docs, expense.folderId!))
                                .filter((folder) => folder !== undefined)

                            // When a folder not expanded AND checkbox disabled AND sub-expenses got selected THEN user should be acknowledged BY expanding the folder.
                            // ref: https://levaroio.atlassian.net/browse/BUG-7150
                            setExpandedRowKeys(subExpensesFolders.map((folder) => folder.id))
                        },
                        getCheckboxProps: (data: Expense) => ({
                            disabled: !checkIsExpenseReviewClickable({
                                expense: data,
                                vendors,
                                isCompanyGobdCompliant: enforceGobdCompliantInvoice,
                                isExpenseAccountMandatory,
                            }),
                        }),
                    }}
                    pagination={{
                        current: reviewTable.data.page,
                        pageSize: reviewTable.data.limit,
                        total: reviewTable.data.totalDocs,
                        position: ["bottomRight"],
                        showSizeChanger: true,
                        hideOnSinglePage: false,
                        pageSizeOptions: ["5", "10", "20", "50", "100"],
                    }}
                    onChange={updateTableOnChange(TablesEnum.REVIEW_REQUESTS, reviewTable.data)}
                    loading={{
                        spinning: reviewTable.isFetching,
                        indicator: <LoadingOutlined style={{ fontSize: 30, color: "black" }} spin />,
                    }}
                    onRow={(record: Expense) => ({
                        onClick: () => {
                            dispatch(setExpense(record))
                            history.push(`/expenses/${record.id}`)
                        },
                    })}
                    expandable={{
                        expandIcon: (props) => <ExpandTripFolderTableIcon props={props} />,
                        expandedRowKeys,
                        onExpandedRowsChange: (expandedRows) => {
                            setExpandedRowKeys([...expandedRows])
                        },
                    }}
                />
            </ul>
        </div>
    )
}
